import React from "react";
import { motion } from "framer-motion";

/**
 * Product Overview (aka Main Components) — dark cards on matte-black bg
 * with orange radial glow on hover. Icon + title + desc, stacked.
 *
 * Used on LPMH page as "Main Components" and on any future product page
 * that wants to showcase sub-assemblies or product breakdown.
 *
 * Props:
 *  - testId
 *  - eyebrow (default "Main Components")
 *  - heading (ReactNode)
 *  - subtitle
 *  - items: { Icon, title, desc }[]
 *  - gridCols: string (default "lg:grid-cols-4")
 */
export default function ProductOverview({
  testId,
  eyebrow = "Main Components",
  heading,
  subtitle,
  items = [],
  gridCols = "lg:grid-cols-4",
}) {
  return (
    <section
      data-testid={testId}
      className="relative bg-liftile-matte-black py-24 md:py-32 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-px bg-liftile-orange/30"
      />
      <div
        aria-hidden
        className="absolute -bottom-32 -right-32 w-[520px] h-[520px] rounded-full pointer-events-none opacity-[0.12]"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(60px)",
        }}
      />

      <div className="container-x relative">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.65 }}
          className="max-w-3xl mb-14 md:mb-16"
        >
          <div className="heading-eyebrow mb-3">{eyebrow}</div>
          <h2 className="display-2 text-liftile-cream">{heading}</h2>
          {subtitle && (
            <p className="font-body text-base md:text-lg text-liftile-cream/70 mt-6 leading-relaxed">
              {subtitle}
            </p>
          )}
        </motion.div>

        <div
          className={`grid grid-cols-1 sm:grid-cols-2 ${gridCols} gap-6 md:gap-7`}
        >
          {items.map((c, i) => (
            <motion.article
              key={c.title}
              data-testid={`${testId}-card-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{
                duration: 0.55,
                delay: i * 0.05,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="group relative h-full flex flex-col rounded-2xl bg-white/[0.03] border border-white/[0.08] p-7 md:p-8 hover:border-liftile-orange/50 hover:bg-white/[0.06] hover:-translate-y-1 transition-all duration-500 overflow-hidden"
            >
              <span
                aria-hidden
                className="absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle, rgba(255,107,26,0.25) 0%, rgba(255,107,26,0) 70%)",
                  filter: "blur(30px)",
                }}
              />
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-liftile-orange/15 flex items-center justify-center group-hover:bg-liftile-orange transition-colors duration-500">
                  <c.Icon
                    className="w-5 h-5 text-liftile-orange group-hover:text-white transition-colors duration-500"
                    strokeWidth={1.6}
                  />
                </div>
                <h3 className="font-heading text-[15px] uppercase tracking-wider font-semibold mt-5 text-liftile-cream leading-snug">
                  {c.title}
                </h3>
                <p className="font-body text-[13.5px] text-liftile-cream/65 mt-3 leading-relaxed flex-1">
                  {c.desc}
                </p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
